import { HolidayDateGlyph } from "./EGDayClosure";
import { dayClosure, openDayShifts } from "../../lib/egDayClosures";
import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { shiftHours } from "../../lib/egScheduleDisplay";
import { EG_SHIFT_COLORS, EG_SHIFT_LABELS, getShiftPresentation } from "../../lib/egShiftPresentation";

const wordmarks = {PLT:"plantation", FLL:"fort-lauderdale", WYN:"wynwood", PNC:"pinecrest", CC:"cherry-creek", RNO:"rino"};

const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function isSalariedGeneralManager(person) {
  const details = [person?.role, person?.job_title, person?.title, person?.employment_type]
    .filter(Boolean).join(" ").toLowerCase();
  const firstName = String(person?.employee_name || person?.name || "").trim().split(/\s+/)[0].toLowerCase();
  return /general\s*manager|\bgm\b|salaried/.test(details) || firstName === "damon";
}

function parseISO(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return !Number.isNaN(parsed.getTime()) && isoDate(parsed) === value ? parsed : null;
}

function addDays(date, count) {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
}

function isoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function mondayOf(date) {
  const next = new Date(date);
  next.setDate(next.getDate() - ((next.getDay() + 6) % 7));
  return next;
}

function totalHours(shifts) {
  const total = shifts.reduce((sum, shift) => sum + shiftHours(shift), 0);
  return Number.isInteger(total) ? String(total) : total.toFixed(1).replace(/\.0$/, "");
}

function monthDay(date) {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function dateLabel(date) {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function weeksInRange(startDate, endDate) {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  if (!start || !end || start > end) return [];
  const weeks = [];
  for (let week = mondayOf(start); week <= end; week = addDays(week, 7)) weeks.push(week);
  return weeks;
}

function PrintShift({ shift }) {
  const presentation = getShiftPresentation(shift);
  const needsTimes = shift && !presentation.hasTimes && ["opening", "mid_shift", "closing", "training"].includes(presentation.type);
  return (
    <div
      className={`eg-print-shift${presentation.requested ? " eg-print-requested" : ""}`}
      data-shift-type={presentation.type}
      data-requested={presentation.requested || undefined}
      style={{ borderLeftColor: EG_SHIFT_COLORS[presentation.type] }}
      data-mod={shift?.mod === true || undefined}
    >
      {shift?.mod === true && <i className="eg-print-mod-rail" aria-label="MOD" />}
      <div className="eg-print-shift-label">{presentation.label}</div>
      {presentation.hasTimes && <div className="eg-print-shift-time">{presentation.timeLabel}</div>}
      {needsTimes && <div className="eg-print-note">Time not set</div>}
      {shift?.notes && <div className="eg-print-note">{shift.notes}</div>}
    </div>
  );
}

/**
 * The browser's print dialog turns this into a clean PDF packet: one weekly
 * schedule per landscape page for the requested inclusive date range. The
 * body portal keeps the packet outside the vessel's clipped scroll surface.
 */
export default function EGPrintPacket({ period, team = [], startDate, endDate }) {
  const rangeStart = startDate || period?.period_start;
  const rangeEnd = endDate || period?.period_end;
  const weeks = useMemo(() => weeksInRange(rangeStart, rangeEnd), [rangeStart, rangeEnd]);
  const shiftsByCell = useMemo(() => {
    const result = {};
    for (const shift of openDayShifts(period?.current_version)) {
      const key = `${shift.employee_id}|${shift.date}`;
      if (!result[key]) result[key] = [];
      result[key].push(shift);
    }
    return result;
  }, [period?.current_version]);

  if (!period || weeks.length === 0 || typeof document === "undefined") return null;

  return createPortal(
    <section id="eg-print-packet" aria-hidden="true">
      <style>{`
        #eg-print-packet { display: none; }
        @media print {
          @page { size: 11in 8.5in; margin: 10mm; }
          html, body { height: auto !important; min-height: 0 !important; overflow: visible !important; background: white !important; }
          body { margin: 0 !important; padding: 0 !important; }
          body > :not(#eg-print-packet) { display: none !important; }
          body::before, body::after { display: none !important; }
          #eg-print-packet, #eg-print-packet * { visibility: visible !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          #eg-print-packet { display: block !important; position: static; width: 100%; color: #142B40; font-family: -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif; }
          .eg-print-page { break-after: page; page-break-after: always; }
          .eg-print-page:last-child { break-after: auto; page-break-after: auto; }
          .eg-print-table tr { break-inside: avoid; page-break-inside: avoid; }
          .eg-print-title { display: flex; justify-content: space-between; gap: 12mm; align-items: end; padding-bottom: 4mm; }
          .eg-print-brand { font-size: 10pt; font-weight: 600; }
          .eg-print-heading { font-family: Georgia, 'Times New Roman', serif; font-size: 22pt; margin-top: 2mm; }
          .eg-print-wordmark{width:60mm;max-height:17mm;object-fit:contain;}
          .eg-print-dates{font-size:10pt;margin-top:2mm;}
          .eg-print-meta { font-size: 9pt; text-align: right; line-height: 1.45; color: #404040; }
          .eg-print-table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 8.5pt; }
          .eg-print-table th, .eg-print-table td { border-bottom: .25mm solid #DDE3E7; padding: 1.6mm 1.3mm; vertical-align: top; }
          .eg-print-table td + td { border-left: .25mm solid #E5E9EC; }
          .eg-print-table th { background: white; text-align: left; font-size: 6.8pt; font-weight: 600; line-height: 1.5; text-transform: uppercase; }
          .eg-print-table th:first-child, .eg-print-table td:first-child { width: 14%; text-align: left; }
          .eg-print-table th:last-child, .eg-print-table td:last-child { width: 6%; text-align: center; }
          .eg-print-name { font-size: 8.5pt; font-weight: 600; overflow-wrap: anywhere; }
          .eg-print-shift { position:relative; background: white; color: #142B40; border-left: 1mm solid; padding: .8mm 1.2mm; min-height: 5mm; overflow-wrap: anywhere; break-inside: avoid; }
          .eg-print-mod-rail {position:absolute;top:0;bottom:0;left:.35mm;width:.7mm;background:#111;}
          .eg-print-shift[data-mod=true]{padding-left:2mm;}
          .eg-print-shift + .eg-print-shift { margin-top: 2mm; }
          .eg-print-shift-label { font-size: 7pt; line-height: 1.3; font-weight: 400; }
          .eg-print-shift-time { margin-top: .6mm; font-size: 7.1pt; font-weight: 700; line-height: 1.3; }
          .eg-print-requested { outline: .25mm dashed ${EG_SHIFT_COLORS.unavailable}; outline-offset: .45mm; }
          .eg-print-request-caption { margin-top: .6mm; font-size: 6.8pt; line-height: 1.2; }
          .eg-print-outside { color: #777; text-align: center; }
          .eg-print-note { margin-top: 1.2mm; color: #555; font-size: 7pt; line-height: 1.2; }
          .eg-print-total { font-size: 10pt; font-weight: 700; }
          .eg-print-role { margin-top: 1mm; color: #666; font-size: 6.7pt; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
          .eg-print-legend { margin-top: 3mm; margin-bottom: 0; padding-bottom: 2mm; border-bottom: .25mm solid #DDE3E7; display: flex; flex-wrap: wrap; align-items: center; gap: 2mm 4mm; font-size: 7pt; color: #142B40; }
          .eg-print-key { display: inline-flex; align-items: center; gap: 1.2mm; }
          .eg-print-swatch { width: 1mm; height: 4mm; }
          .eg-print-request-swatch { width: 4mm; height: 3mm; border: .25mm dashed ${EG_SHIFT_COLORS.unavailable}; }
          .eg-print-exclusion { margin-top: 3mm; color: #666; font-size: 7pt; }
          .eg-print-footer { margin-top: 4mm; display: flex; justify-content: space-between; gap: 4mm; font-size: 7pt; color: #555; }
        }
      `}</style>
      {weeks.map((weekStart) => {
        const days = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
        return (
          <article className="eg-print-page" key={isoDate(weekStart)} data-week-start={isoDate(weekStart)}>
            <header className="eg-print-title">
              <div>
                <div className="eg-print-heading">Experience Guide Schedule</div>
                <div className="eg-print-dates">{dateLabel(weekStart)} – {dateLabel(days[6])}</div>
              </div>
              <div className="eg-print-meta">
                {wordmarks[period.boutique_code] ? <img className="eg-print-wordmark" src={`/brand/boutiques/${wordmarks[period.boutique_code]}-wordmark.svg`} alt={period.boutique_name || period.boutique_code} /> : <div>{period.boutique_name || period.boutique_code}</div>}
                <div>{period.status?.replace(/_/g," ")} · Saved version {period.current_version?.version_number || 1}</div>
              </div>
            </header>
            <table className="eg-print-table">
              <thead>
                <tr>
                  <th>Experience Guide</th>
                  {days.map((day, index) => <th key={isoDate(day)} className={dayClosure(period.current_version,isoDate(day))?"eg-print-holiday-head":""}>{dayNames[index]}{dayClosure(period.current_version,isoDate(day))&&<HolidayDateGlyph glyph={dayClosure(period.current_version,isoDate(day)).glyph}/>}<br />{monthDay(day)}{dayClosure(period.current_version,isoDate(day))&&" · Holiday"}</th>)}
                  <th>Hours</th>
                </tr>
              </thead>
              <tbody>
                {team.map((guide) => {
                  const includedDays = days.map((day) => isoDate(day) >= rangeStart && isoDate(day) <= rangeEnd);
                  const rowShifts = days.map((day, index) => includedDays[index] ? shiftsByCell[`${guide.employee_id}|${isoDate(day)}`] || [] : []);
                  const salariedGM = isSalariedGeneralManager(guide);
                  return (
                    <tr key={guide.employee_id} data-employee-id={guide.employee_id}>
                      <td>
                        <div className="eg-print-name">{guide.employee_name || guide.name}</div>
                        {salariedGM && <div className="eg-print-role">Salaried GM</div>}
                      </td>
                      {rowShifts.map((shifts, index) => {
                        return <td key={isoDate(days[index])} data-date={isoDate(days[index])} className={dayClosure(period.current_version,isoDate(days[index]))?"eg-print-closed":""}>
                          {dayClosure(period.current_version,isoDate(days[index])) ? null : !includedDays[index] ? <div className="eg-print-outside">—</div> : shifts.length ? shifts.map((shift, shiftIndex) => <PrintShift key={shift.id || shiftIndex} shift={shift} />) : <PrintShift />}
                        </td>;
                      })}
                      <td><div className="eg-print-total">{salariedGM ? "—" : totalHours(rowShifts.flat())}</div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="eg-print-footer"><span>Salaried GM shifts are shown, excluded from totals.</span><strong>Week total · {totalHours(days.flatMap(day => {const date=isoDate(day); return date >= rangeStart && date <= rangeEnd ? team.filter(guide=>!isSalariedGeneralManager(guide)).flatMap(guide=>shiftsByCell[`${guide.employee_id}|${date}`] || []) : [];}))} hrs</strong></div>
            <div className="eg-print-exclusion">A 30-minute break is deducted from shifts longer than six hours. Grey dates are outside the selected range.</div>
            <div className="eg-print-legend">
              {Object.entries(EG_SHIFT_COLORS).map(([type, color]) => (
                <span key={type} className="eg-print-key" data-legend-type={type}><span className="eg-print-swatch" style={{ backgroundColor: color }} />{EG_SHIFT_LABELS[type]}</span>
              ))}
              <span className="eg-print-key"><span className="eg-print-request-swatch" />Requested</span>
              <span className="eg-print-key"><span className="eg-print-swatch" style={{backgroundColor:"#111"}} />MOD</span>
            </div>

            <footer className="eg-print-footer">
              <span>{period.boutique_name || period.boutique_code} · Experience Guides</span>
              <span>Hours cover selected dates only · For internal team reference</span>
            </footer>
          </article>
        );
      })}
    </section>, document.body
  );
}
